/**
 * @fileoverview Consistency Verifier for comparing state between two engines
 *
 * Provides utilities to compare game state, detect piece ID changes,
 * and verify synchronization between Light and Dark engines.
 */

import { GameState, ISquare, IPiece, PieceType, Position } from "../../zodiacEngine/types";
import {
  ConsistencyCheck,
  PieceIdChange,
  BonusTurnSyncResult,
  EngineStateSnapshot,
} from "./FirebaseTestTypes";

/**
 * Verify state consistency between two engines
 */
export function verifyStateConsistency(
  lightState: GameState,
  darkState: GameState
): ConsistencyCheck[] {
  const checks: ConsistencyCheck[] = [];

  // Import ZodiacFEN lazily
  const { ZodiacFEN } = require("../../zodiacEngine/Fen/ZodiacFEN");

  // Check FEN
  const lightFEN = ZodiacFEN.encode(lightState);
  const darkFEN = ZodiacFEN.encode(darkState);
  const lightNormFEN = normalizeFEN(lightFEN);
  const darkNormFEN = normalizeFEN(darkFEN);

  // Detailed FEN comparison logging
  const fenMatches = lightNormFEN === darkNormFEN;
  if (!fenMatches) {
    // Find exact difference
    for (let i = 0; i < Math.max(lightNormFEN.length, darkNormFEN.length); i++) {
      if (lightNormFEN[i] !== darkNormFEN[i]) {
        break;
      }
    }
  }

  checks.push({
    field: "fen",
    lightValue: lightFEN,
    darkValue: darkFEN,
    matches: fenMatches,
    discrepancy: !fenMatches ? `FEN mismatch: Light="${lightNormFEN}" vs Dark="${darkNormFEN}"` : undefined,
  });

  // Check turn
  const turnMatches = lightState.isLightTurn === darkState.isLightTurn;

  checks.push({
    field: "turn",
    lightValue: lightState.isLightTurn,
    darkValue: darkState.isLightTurn,
    matches: turnMatches,
    discrepancy:
      !turnMatches
        ? `Turn mismatch: Light=${lightState.isLightTurn}, Dark=${darkState.isLightTurn}`
        : undefined,
  });

  // Check turn count - critical for multi-turn and bonus turn tests!
  const lightTurnCount = lightState.turnCount ?? 0;
  const darkTurnCount = darkState.turnCount ?? 0;
  const turnCountMatches = lightTurnCount === darkTurnCount;
  if (!turnCountMatches) {
  }

  checks.push({
    field: "turnCount",
    lightValue: lightTurnCount,
    darkValue: darkTurnCount,
    matches: turnCountMatches,
    discrepancy:
      !turnCountMatches
        ? `Turn count mismatch: Light=${lightTurnCount}, Dark=${darkTurnCount} (diff: ${Math.abs(lightTurnCount - darkTurnCount)})`
        : undefined,
  });

  // Check bonus turn state
  const lightSkip = lightState.skipTurnChange ?? false;
  const darkSkip = darkState.skipTurnChange ?? false;
  const skipMatches = lightSkip === darkSkip;

  checks.push({
    field: "bonusTurn",
    lightValue: lightSkip,
    darkValue: darkSkip,
    matches: skipMatches,
    discrepancy:
      !skipMatches
        ? `Bonus turn mismatch: Light=${lightSkip}, Dark=${darkSkip}`
        : undefined,
  });

  // Check active bonus turn piece (just whether set or not, not exact ID)
  const lightHasPiece = !!lightState.activeBonusTurnPieceId;
  const darkHasPiece = !!darkState.activeBonusTurnPieceId;
  const pieceIdMatches = lightHasPiece === darkHasPiece;

  checks.push({
    field: "activePieceId",
    lightValue: lightHasPiece ? "[set]" : "[null]",
    darkValue: darkHasPiece ? "[set]" : "[null]",
    matches: pieceIdMatches,
    discrepancy:
      !pieceIdMatches
        ? `Active piece ID mismatch: Light=${lightHasPiece}, Dark=${darkHasPiece}`
        : undefined,
  });

  // Check move history - allow 1 entry difference since Firebase adds current FEN
  // The non-moving engine syncs FROM Firebase which includes the current position
  // The moving engine only adds to history at turn end, so it's 1 behind
  const lightHistoryLen = lightState.fenHistory?.length ?? 0;
  const darkHistoryLen = darkState.fenHistory?.length ?? 0;
  const historyDiff = Math.abs(lightHistoryLen - darkHistoryLen);
  // Allow exact match OR 1-entry difference (expected due to Firebase sync timing)
  const historyMatches = historyDiff <= 1;

  if (historyDiff === 1) {
  }
  if (!historyMatches || lightHistoryLen > 0 || darkHistoryLen > 0) {
    (lightState.fenHistory || []).forEach((fen, i) => {
    });
    (darkState.fenHistory || []).forEach((fen, i) => {
    });
  }

  checks.push({
    field: "moveHistory",
    lightValue: lightHistoryLen,
    darkValue: darkHistoryLen,
    matches: historyMatches,
    discrepancy:
      !historyMatches
        ? `History length mismatch too large: Light=${lightHistoryLen}, Dark=${darkHistoryLen} (diff=${historyDiff}, max allowed=1)`
        : undefined,
  });

  // Summary
  const allMatch = checks.every(c => c.matches);
  checks.filter(c => !c.matches).forEach(c => {
  });

  return checks;
}

/**
 * Verify bonus turn synchronization between two engines
 */
export function verifyBonusTurnSync(
  lightState: GameState,
  darkState: GameState
): BonusTurnSyncResult {
  const lightSkip = lightState.skipTurnChange ?? false;
  const darkSkip = darkState.skipTurnChange ?? false;
  const lightPieceId = lightState.activeBonusTurnPieceId ?? null;
  const darkPieceId = darkState.activeBonusTurnPieceId ?? null;

  // Check if skipTurnChange matches
  const skipMatches = lightSkip === darkSkip;

  // For pieceId, both should be set or both null
  // Actual IDs may differ due to Firebase sync regenerating them
  const pieceIdConsistent =
    (lightPieceId === null && darkPieceId === null) ||
    (lightPieceId !== null && darkPieceId !== null);

  const synced = skipMatches && pieceIdConsistent;

  let details: string | undefined;
  if (!synced) {
    const issues: string[] = [];
    if (!skipMatches) {
      issues.push(`skipTurnChange mismatch (Light=${lightSkip}, Dark=${darkSkip})`);
    }
    if (!pieceIdConsistent) {
      issues.push(`activePieceId inconsistent (Light=${lightPieceId}, Dark=${darkPieceId})`);
    }
    details = issues.join("; ");
  }

  return {
    synced,
    lightSkipTurnChange: lightSkip,
    darkSkipTurnChange: darkSkip,
    lightActivePieceId: lightPieceId,
    darkActivePieceId: darkPieceId,
    details,
  };
}

/**
 * Reconcile a piece ID after Firebase sync
 * Finds a piece by position, type, and color when ID has changed
 */
export function reconcilePieceId(
  originalPosition: Position,
  originalType: PieceType,
  originalIsLight: boolean,
  newBoard: ISquare[][]
): { newId: string | null; method: "exact" | "position" | "type_color" | "failed" } {
  const square = newBoard[originalPosition.row]?.[originalPosition.col];
  if (!square || !square.Pieces || square.Pieces.length === 0) {
    return { newId: null, method: "failed" };
  }

  // Look for exact match by type and color at the same position
  const matchingPiece = square.Pieces.find(
    (p) => p.type === originalType && p.isLight === originalIsLight
  );

  if (matchingPiece) {
    return { newId: matchingPiece.id, method: "position" };
  }

  // If not found at position, try searching entire board
  for (let row = 0; row < newBoard.length; row++) {
    for (let col = 0; col < newBoard[row].length; col++) {
      const sq = newBoard[row][col];
      if (!sq.Pieces) continue;

      for (const piece of sq.Pieces) {
        if (piece.type === originalType && piece.isLight === originalIsLight) {
          // Found by type and color but different position - this shouldn't happen
          // for normal reconciliation but handle it anyway
          return { newId: piece.id, method: "type_color" };
        }
      }
    }
  }

  return { newId: null, method: "failed" };
}

/**
 * Track all piece ID changes between two board states
 */
export function trackPieceIdChanges(
  beforeBoard: ISquare[][],
  afterBoard: ISquare[][]
): PieceIdChange[] {
  const changes: PieceIdChange[] = [];

  // Build a map of all pieces before by position
  const beforePieces = new Map<string, IPiece[]>();
  for (let row = 0; row < beforeBoard.length; row++) {
    for (let col = 0; col < beforeBoard[row].length; col++) {
      const square = beforeBoard[row][col];
      if (square.Pieces && square.Pieces.length > 0) {
        beforePieces.set(`${row},${col}`, [...square.Pieces]);
      }
    }
  }

  // Compare with after state
  for (let row = 0; row < afterBoard.length; row++) {
    for (let col = 0; col < afterBoard[row].length; col++) {
      const afterSquare = afterBoard[row][col];
      if (!afterSquare.Pieces || afterSquare.Pieces.length === 0) continue;

      const posKey = `${row},${col}`;
      const beforeSquarePieces = beforePieces.get(posKey) || [];

      for (const afterPiece of afterSquare.Pieces) {
        // Find matching piece in before state
        const beforePiece = beforeSquarePieces.find(
          (p) => p.type === afterPiece.type && p.isLight === afterPiece.isLight
        );

        if (beforePiece && beforePiece.id !== afterPiece.id) {
          changes.push({
            pieceType: afterPiece.type,
            position: { row, col },
            beforeId: beforePiece.id,
            afterId: afterPiece.id,
            resolvedBy: "position",
          });
        } else if (!beforePiece) {
          // Piece appeared at this position - could be a move or respawn
          // Try to find it elsewhere in the before board
          let found = false;
          for (const [, pieces] of beforePieces) {
            const match = pieces.find(
              (p) => p.type === afterPiece.type && p.isLight === afterPiece.isLight
            );
            if (match && match.id !== afterPiece.id) {
              changes.push({
                pieceType: afterPiece.type,
                position: { row, col },
                beforeId: match.id,
                afterId: afterPiece.id,
                resolvedBy: "type_color",
              });
              found = true;
              break;
            }
          }

          if (!found) {
            // Completely new piece (shouldn't happen in normal gameplay)
            changes.push({
              pieceType: afterPiece.type,
              position: { row, col },
              beforeId: "[new]",
              afterId: afterPiece.id,
              resolvedBy: "failed",
            });
          }
        }
      }
    }
  }

  return changes;
}

/**
 * Compare two board states for piece-level equality
 */
export function compareBoardStates(
  board1: ISquare[][],
  board2: ISquare[][]
): { matches: boolean; differences: string[] } {
  const differences: string[] = [];

  // Check dimensions
  if (board1.length !== board2.length) {
    differences.push(`Row count mismatch: ${board1.length} vs ${board2.length}`);
    return { matches: false, differences };
  }

  for (let row = 0; row < board1.length; row++) {
    if (board1[row].length !== board2[row].length) {
      differences.push(`Column count mismatch at row ${row}`);
      continue;
    }

    for (let col = 0; col < board1[row].length; col++) {
      const sq1 = board1[row][col];
      const sq2 = board2[row][col];

      const pieces1 = sq1.Pieces || [];
      const pieces2 = sq2.Pieces || [];

      if (pieces1.length !== pieces2.length) {
        differences.push(
          `Piece count mismatch at (${row},${col}): ${pieces1.length} vs ${pieces2.length}`
        );
        continue;
      }

      // Compare pieces by type and color (not ID)
      for (let i = 0; i < pieces1.length; i++) {
        const p1 = pieces1[i];
        const p2Matches = pieces2.some(
          (p2) =>
            p2.type === p1.type &&
            p2.isLight === p1.isLight &&
            (p1.zombifiedStatus?.isZombified ?? false) ===
              (p2.zombifiedStatus?.isZombified ?? false)
        );

        if (!p2Matches) {
          differences.push(
            `Piece mismatch at (${row},${col}): ` +
              `${p1.isLight ? "Light" : "Dark"} ${PieceType[p1.type]} not found in second board`
          );
        }
      }
    }
  }

  return { matches: differences.length === 0, differences };
}

/**
 * Normalize FEN for comparison (remove turn indicator and status symbols)
 */
function normalizeFEN(fen: string): string {
  let normalized = fen.trim();

  // Remove turn indicator prefix
  if (normalized.startsWith("d") || normalized.startsWith("l")) {
    normalized = normalized.substring(1);
  }

  // Remove status symbols for core comparison
  normalized = normalized.replace(/[✳️*○$⛓☠!☣#z↑↓←→↖↗↘↙]/g, "");

  // Normalize king symbols
  normalized = normalized.replace(/([A-Z])♛/g, "$1K");
  normalized = normalized.replace(/([a-z])♕/g, "$1k");
  normalized = normalized.replace(/([A-Z])♕/g, "$1K");
  normalized = normalized.replace(/([a-z])♛/g, "$1k");

  return normalized;
}

/**
 * Extract all pieces from a board
 */
export function extractAllPieces(board: ISquare[][]): Array<{
  piece: IPiece;
  position: Position;
}> {
  const result: Array<{ piece: IPiece; position: Position }> = [];

  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (square.Pieces) {
        for (const piece of square.Pieces) {
          result.push({ piece, position: { row, col } });
        }
      }
    }
  }

  return result;
}

/**
 * Count pieces on a board
 */
export function countPieces(board: ISquare[][]): { light: number; dark: number; total: number } {
  let light = 0;
  let dark = 0;

  for (const row of board) {
    for (const square of row) {
      if (square.Pieces) {
        for (const piece of square.Pieces) {
          if (piece.isLight) {
            light++;
          } else {
            dark++;
          }
        }
      }
    }
  }

  return { light, dark, total: light + dark };
}
